import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import DashboardLayout from './DashboardLayout';
import StatsCards from './StatsCards';
import KPIMetrics from './KPIMetrics';
import RecipesTable from './RecipesTable';
import UsersTable from './UsersTable';
import CategoriesTable from './CategoriesTable';
import SubcategoriesTable from './SubcategoriesTable';
import UnitsTable from './UnitsTable';
import EquipmentTable from './EquipmentTable';
import ChefApplicationsTable from './ChefApplicationsTable';
import PostsTable from './PostsTable';
import CommunityRecipesTable from './CommunityRecipesTable';
import OrdersTable from './OrdersTable';
import ManualPaymentsTable from './ManualPaymentsTable';
import PaymentInstructionsTable from './PaymentInstructionsTable';
import InventoryTable from './InventoryTable';
import ContactTable from './ContactTable';
import SupportRequestsTable from './SupportRequestsTable';
import Settings from './Settings';
import ChefOnDemandSettings from './ChefOnDemandSettings';
import CuisineCatalogSettings from './CuisineCatalogSettings';
import SpecialtyCatalogSettings from './SpecialtyCatalogSettings';
import LanguageCatalogSettings from './LanguageCatalogSettings';
import BannersTable from './BannersTable';
import HygieneSafetySettings from './HygieneSafetySettings';
import PromoCodesTable from './PromoCodesTable';
import IngredientCategoriesTable from './IngredientCategoriesTable';
import IngredientsTable from './IngredientsTable';
import GlobalNotificationsPanel from './GlobalNotificationsPanel';

type Tab = 'home' | 'metrics' | 'recipes' | 'categories' | 'subcategories' | 'users' | 'units' | 'equipments' | 'chefs' | 'posts' | 'communityRecipes' | 'chefRequests' | 'cuisines' | 'specialties' | 'chefLanguages' | 'chefOnDemand' | 'orders' | 'manualPayments' | 'paymentInstructions' | 'hygieneSafety' | 'promoCodes' | 'inventory' | 'contact' | 'supportRequests' | 'banners' | 'globalNotifications' | 'settings' | 'pantryCategories' | 'pantryIngredients';

export default function Dashboard() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const tabParam = searchParams.get('tab') as Tab | null;
  const [activeTab, setActiveTab] = useState<Tab>(tabParam || 'home');

  useEffect(() => {
    if (tabParam && ['home', 'metrics', 'recipes', 'categories', 'subcategories', 'users', 'units', 'equipments', 'chefs', 'posts', 'communityRecipes', 'chefRequests', 'cuisines', 'specialties', 'chefLanguages', 'chefOnDemand', 'orders', 'manualPayments', 'paymentInstructions', 'hygieneSafety', 'promoCodes', 'inventory', 'contact', 'supportRequests', 'banners', 'globalNotifications', 'settings', 'pantryCategories', 'pantryIngredients'].includes(tabParam)) {
      setActiveTab(tabParam);
    } else {
      setActiveTab('home');
    }
  }, [tabParam]);

  const layoutSubtitle =
    activeTab === 'globalNotifications' ? t('layout.subtitleGlobalNotifications') : undefined;

  return (
    <DashboardLayout subtitle={layoutSubtitle}>
      {activeTab === 'home' && <StatsCards />}
      {activeTab === 'metrics' && <KPIMetrics />}
      {activeTab === 'recipes' && <RecipesTable />}
      {activeTab === 'categories' && <CategoriesTable />}
      {activeTab === 'subcategories' && <SubcategoriesTable />}
      {activeTab === 'users' && <UsersTable />}
      {activeTab === 'orders' && <OrdersTable />}
      {activeTab === 'manualPayments' && <ManualPaymentsTable />}
      {activeTab === 'paymentInstructions' && <PaymentInstructionsTable />}
      {activeTab === 'hygieneSafety' && <HygieneSafetySettings />}
      {activeTab === 'promoCodes' && <PromoCodesTable />}
      {activeTab === 'inventory' && <InventoryTable />}
      {activeTab === 'contact' && <ContactTable />}
      {activeTab === 'supportRequests' && <SupportRequestsTable />}
      {activeTab === 'chefs' && <ChefApplicationsTable />}
      {activeTab === 'chefRequests' && <ChefApplicationsTable />}
      {activeTab === 'posts' && <PostsTable />}
      {activeTab === 'communityRecipes' && <CommunityRecipesTable />}
      {activeTab === 'units' && <UnitsTable />}
      {activeTab === 'equipments' && <EquipmentTable />}
      {activeTab === 'banners' && <BannersTable />}
      {activeTab === 'globalNotifications' && <GlobalNotificationsPanel />}
      {activeTab === 'pantryCategories' && <IngredientCategoriesTable />}
      {activeTab === 'pantryIngredients' && <IngredientsTable />}
      {activeTab === 'settings' && <Settings />}
      {activeTab === 'cuisines' && <CuisineCatalogSettings />}
      {activeTab === 'specialties' && <SpecialtyCatalogSettings />}
      {activeTab === 'chefLanguages' && <LanguageCatalogSettings />}
      {activeTab === 'chefOnDemand' && <ChefOnDemandSettings />}
    </DashboardLayout>
  );
}
